import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

/**
 * Per-location logos managed from POS Configuration. Each store uploads and
 * keeps its own, stored on its pos_tenant_config row and pushed to that
 * location's registers:
 *   receipt-logo  grayscale artwork printed on receipts (receipt_logo_path)
 *   cds-logo      colour artwork shown on the customer display (cds_logo_path)
 *
 * Distinct from the tenant's colour brand mark (useBusinessBranding().logoUrl),
 * which is super-admin owned. A merchant owner/admin may manage these.
 */
export type LocationLogoKind = 'receipt-logo' | 'cds-logo';

export interface LocationLogo {
  locationId: string;
  kind: LocationLogoKind;
  url: string | null;
}

export const locationLogoKey = (kind: LocationLogoKind, tenantId: string, locationId: string) =>
  ['location-logo', kind, tenantId, locationId] as const;

export function useLocationLogo(kind: LocationLogoKind, tenantId: string, locationId: string) {
  return useQuery<{ data: LocationLogo }>({
    queryKey: locationLogoKey(kind, tenantId, locationId),
    queryFn: () => api.get(`/admin/branding/${tenantId}/locations/${locationId}/${kind}`),
    enabled: !!tenantId && !!locationId,
  });
}

type LogoScope = { kind: LocationLogoKind; tenantId: string; locationId: string };

/**
 * Upload a store's logo. For the receipt logo, [file] is expected to be
 * already grayscaled by the caller (see lib/receipt-logo.ts), so what the
 * merchant previewed is what gets stored.
 */
export function useUploadLocationLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ kind, tenantId, locationId, file }: LogoScope & { file: Blob }) => {
      const formData = new FormData();
      formData.append('file', file, `${kind}.png`);
      const response = await fetch(`/api/v1/admin/branding/${tenantId}/locations/${locationId}/${kind}`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
        throw error;
      }
      return response.json() as Promise<{ data: LocationLogo; posPushOk: boolean }>;
    },
    onSuccess: async (_data, { kind, tenantId, locationId }) => {
      await queryClient.invalidateQueries({ queryKey: locationLogoKey(kind, tenantId, locationId) });
    },
  });
}

/** Remove a store's logo entirely. Its registers drop it on their next sync. */
export function useDeleteLocationLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, tenantId, locationId }: LogoScope) =>
      api.delete<{ data: LocationLogo; posPushOk: boolean }>(
        `/admin/branding/${tenantId}/locations/${locationId}/${kind}`,
      ),
    onSuccess: async (_data, { kind, tenantId, locationId }) => {
      await queryClient.invalidateQueries({ queryKey: locationLogoKey(kind, tenantId, locationId) });
    },
  });
}
